import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import { QrCodePanel } from '@/components/payment/QrCodePanel';
import { BankIdLaunchPanel } from '@/components/shared/BankIdLaunchPanel';
import { SwitchToSameDeviceButton } from '@/components/shared/SwitchToSameDeviceButton';
import { ConsentService } from '@/services/consentService';
import { useConsentPolling } from '@/hooks/useConsentPolling';
import { savePendingConsentResume } from '@/lib/bankConsentResume';
import { useTranslations } from 'use-intl';
import type { AccountType } from '@/types/accounts';

interface ConsentAuthFlowProps {
  bic: string;
  /** Provider-native reference for the institution the user picked (FIN-592). Optional: without
   *  it the backend falls back to resolving by bic, which cannot disambiguate siblings. */
  bankRef?: string;
  /** ISO 3166-1 alpha-2 country the institution was picked in (FIN-783) — required alongside
   *  bankRef to resolve a bic-less bank, whose bic is the empty-string sentinel. */
  countryCode?: string;
  /** Always start a fresh authorization, even when a valid consent exists ("Update access", FIN-863). */
  forceNew?: boolean;
  /** FIN-1567: which login to request -- absent means business. */
  accountType?: AccountType;
  /** `isExisting`: the backend reused a valid consent instead of sending the user to the bank. */
  onConsentCompleted: (consentId: string, outcome: { isExisting: boolean }) => void;
  onError: (error: string | Error | unknown) => void;
}

export const ConsentAuthFlow: React.FC<ConsentAuthFlowProps> = ({
  bic,
  bankRef,
  countryCode,
  forceNew,
  accountType,
  onConsentCompleted,
  onError
}) => {
  const t = useTranslations('consultantBank.consent');
  const [consentData, setConsentData] = useState<any>(null);
  const [isInitiating, setIsInitiating] = useState(false);
  const [hasAutoInitiated, setHasAutoInitiated] = useState(false);
  const [usingSameDevice, setUsingSameDevice] = useState(false);

  const consentService = new ConsentService();

  // Use polling hook with dynamic interval
  const pollingData = useConsentPolling(
    consentData?.consentId || '',
    consentData?.authorisationId || '',
    bic,
    {
      refreshInterval: consentData?.refreshInterval,
      qrToken: consentData?.qrToken,
      qrCodeImage: consentData?.qrCodeImage,
      bankIdUrl: consentData?.bankIdUrl,
      scaRedirect: consentData?.scaRedirect
    },
    bankRef,
    countryCode
  );

  // Handle consent completion. A pre-existing valid consent (consentData.isExisting) has no SCA
  // challenge to poll for at all -- useConsentPolling's needsPolling never starts an interval in
  // that case, so pollingData.isCompleted would otherwise never become true and the flow would
  // sit on the "Existing Consent" badge forever. Complete immediately in that case.
  React.useEffect(() => {
    if ((pollingData.isCompleted || consentData?.isExisting) && consentData?.consentId) {
      onConsentCompleted(consentData.consentId, { isExisting: Boolean(consentData.isExisting) });
    }
  }, [pollingData.isCompleted, consentData?.isExisting, consentData?.consentId, onConsentCompleted]);

  // Handle errors
  React.useEffect(() => {
    if (pollingData.error) {
      onError(pollingData.error);
    }
  }, [pollingData.error, onError]);

  // Auto-initiate with "other device" on mount
  React.useEffect(() => {
    if (!hasAutoInitiated && !consentData) {
      setHasAutoInitiated(true);
      initiateConsent(false); // false = other device
    }
  }, [hasAutoInitiated, consentData]);

  const initiateConsent = async (sameDevice: boolean) => {
    setIsInitiating(true);
    try {
      const result = await consentService.initiateConsentCreation({ bic, sameDevice, ref: bankRef, countryCode, forceNew, accountType });
      setConsentData(result);
      console.log('Consent initiation result:', result);
    } catch (error) {
      console.error('Consent initiation error:', error);
      // Pass the full error object to allow proper error handling
      onError(error);
    } finally {
      setIsInitiating(false);
    }
  };

  const switchToSameDevice = async () => {
    setConsentData(null);
    setUsingSameDevice(true);
    setIsInitiating(true);
    try {
      const result = await consentService.initiateConsentCreation({ bic, sameDevice: true, ref: bankRef, countryCode, forceNew, accountType });
      setConsentData(result);
      console.log('Switched to same device:', result);
    } catch (error) {
      console.error('Same device switch error:', error);
      // Pass the full error object to allow proper error handling
      onError(error);
    } finally {
      setIsInitiating(false);
    }
  };

  const renderAuthMethod = () => {
    if (!consentData) return null;

    // Show existing valid consent
    if (consentData.isExisting) {
      return (
        <div className="text-center">
          <Badge variant="secondary" className="mb-2">{t('existingBadge')}</Badge>
          <p className="text-sm text-muted-foreground">
            {t('existingBody', { bic })}
          </p>
        </div>
      );
    }

    // Priority 1: BankID URL (mbid/mbid_same_device flow)
    if (pollingData.bankIdUrl) {
      return (
        <BankIdLaunchPanel
          badge={consentData.authMethod === 'mbid_same_device' ? t('sameDeviceBadge') : t('mobileBadge')}
          buttonLabel={t('launchBankId')}
          hint={t('launchHint')}
          url={pollingData.bankIdUrl}
        />
      );
    }

    // Priority 2: Generated QR Code from token (mbid_animated_qr_token flow)
    if (pollingData.generatedQrImage) {
      return (
        <QrCodePanel
          badge={t('qrTokenBadge')}
          src={pollingData.generatedQrImage}
          alt={t('qrAlt')}
          hint={t('scanQr')}
          pollingNote={pollingData.isPolling ? t('checkingEvery', { interval: consentData.refreshInterval ?? '' }) : undefined}
        />
      );
    }

    // Priority 3: QR Code Image (mbid_animated_qr_image flow)
    if (pollingData.qrCodeImage) {
      return (
        <QrCodePanel
          badge={t('animatedQrBadge')}
          src={pollingData.qrCodeImage}
          alt={t('qrAlt')}
          hint={t('scanQr')}
          pollingNote={pollingData.isPolling ? t('refreshingEvery', { interval: consentData.refreshInterval ?? '' }) : undefined}
        />
      );
    }

    // Redirect Flow — read directly from consentData so the button appears
    // immediately without waiting for the polling hook's async effect to fire.
    const scaRedirect = consentData.scaRedirect || pollingData.scaRedirect;
    if (scaRedirect) {
      const handleRedirect = () => {
        if (usingSameDevice) {
          savePendingConsentResume({
            bic,
            ref: bankRef,
            countryCode,
            consentId: consentData.consentId,
            authorisationId: consentData.authorisationId,
            accountType
          });
          window.location.href = scaRedirect;
        } else {
          window.open(scaRedirect, '_blank');
        }
      };
      return (
        <div className="space-y-4">
          <div className="text-center">
            <Badge variant="outline" className="mb-2">{t('bankAuthBadge')}</Badge>
          </div>
          <Alert>
            <AlertDescription>{t('selectEveryAccount')}</AlertDescription>
          </Alert>
          <Button
            onClick={handleRedirect}
            className="w-full"
            size="lg"
          >
            {t('authenticate')}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            {usingSameDevice ? t('redirectHintSameDevice') : t('redirectHintOtherDevice')}
          </p>
        </div>
      );
    }

    // Loading state
    return (
      <div className="text-center py-8">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          {t('settingUp')}
        </p>
      </div>
    );
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description', { bic })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!consentData && isInitiating && (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('starting')}
            </p>
          </div>
        )}
        {consentData && (
          <>
            {pollingData.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{pollingData.error}</AlertDescription>
              </Alert>
            )}

            {renderAuthMethod()}

            {pollingData.isPolling && (
              <div className="text-center pt-2">
                <Badge variant="secondary">
                  {t('status', { status: pollingData.scaStatus })}
                </Badge>
              </div>
            )}

            {!consentData.isExisting && (
              <SwitchToSameDeviceButton
                onSwitch={switchToSameDevice}
                isSwitching={isInitiating}
                label={t('useSameDevice')}
                switchingLabel={t('switching')}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ConsentAuthFlow;